import ModalTittle from "@View/components/ModalTittle";
import Tooltip from "@mui/material/Tooltip";
import ModalWrapper from "@View/components/ModalWrapper";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CheckPointIcon from "@View/components/svgComponents/CheckPointIcon";
import EmptyPointIcon from "@View/components/svgComponents/EmptyPointIcon";
import { formatDate } from "@Localization/utils";
import getAPI from "@API/index";

const firstColumnStyles = {
  whiteSpace: "nowrap",
  paddingRight: "15px",
  position: "sticky",
  left: 0,
  background: "#fff",
  fontWeight: 500,
};

const pendingColumnStyles = {
  backgroundColor: "rgba(111, 164, 242, 0.1)",
};

const PreviewGroupConsolidationModal = ({
  customerId,
  group,
  consolidation,
  open,
  handleOpen,
  handleClose,
  reloadData,
}) => {
  const { t } = useTranslation();
  const [matrixData, setMatrixData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [addingPeriod, setAddingPeriod] = useState(null); // ID периода, который добавляется

  const fetchMatrix = async () => {
    if (!consolidation?.id) return;
    setIsLoading(true);
    try {
      const response = await getAPI().request({
        url: `/api/customer/${customerId}/consolidation/${consolidation.id}/matrix`,
      });
      setMatrixData(response);
    } catch (error) {
      console.error("Error fetching consolidation matrix:", error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (open && consolidation?.id) {
      fetchMatrix();
    }
  }, [open, consolidation?.id]);

  const handleAddPeriod = async (period) => {
    setAddingPeriod(period);
    try {
      await getAPI().request({
        url: `/api/customer/${customerId}/consolidation/${consolidation.id}/add-periods`,
        method: "post",
        data: [period],
      });
      if (reloadData) {
        reloadData();
      }
      fetchMatrix();
    } catch (error) {
      console.error("Error adding period:", error);
    }
    setAddingPeriod(null);
  };

  // Объединяем текущие периоды и pending периоды (исключая дубликаты)
  const allPeriods = matrixData
    ? (() => {
        const periods = matrixData.periods || [];
        const periodsSet = new Set(periods.map(p => new Date(p).getTime()));
        const uniquePending = (matrixData.pendingPeriods || [])
          .filter(p => !periodsSet.has(new Date(p).getTime()));
        return [...periods, ...uniquePending]
          .sort((a, b) => new Date(a) - new Date(b));
      })()
    : [];

  const isPendingPeriod = (period) => {
    return matrixData?.pendingPeriods?.some(
      p => new Date(p).getTime() === new Date(period).getTime()
    );
  };

  // Подсчитываем количество компаний с данными для периода
  const getCompaniesWithDataCount = (period) => {
    return matrixData?.companies?.filter(c =>
      Object.entries(c.pendingPeriodAvailability || {})
        .some(([key, value]) =>
          new Date(key).getTime() === new Date(period).getTime() && value
        )
    ).length || 0;
  };

  return (
    <ModalWrapper
      open={open}
      handleOpen={handleOpen}
      handleClose={handleClose}
      maxWidth={900}
      maxHeight={"100%"}
    >
      <div style={{ margin: "auto", maxWidth: "100%", position: "relative" }}>
        <ModalTittle>Периоды консолидации</ModalTittle>

        {isLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            {t("creditScore.loading")}
          </div>
        ) : matrixData ? (
          <>
            {/* Легенда */}
            <div style={{
              display: "flex",
              gap: "20px",
              marginBottom: "15px",
              fontSize: "13px",
              alignItems: "center"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <div style={{
                  width: "16px",
                  height: "16px",
                  backgroundColor: "#fff",
                  border: "1px solid #ddd"
                }} />
                <span>{t("consolidation.existingPeriods") || "Существующие периоды"}</span>
              </div>
              {matrixData.hasPendingPeriods && (
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                  <div style={{
                    width: "16px",
                    height: "16px",
                    backgroundColor: "rgba(111, 164, 242, 0.2)",
                    border: "1px solid rgba(111, 164, 242, 0.5)"
                  }} />
                  <span>{t("consolidation.pendingPeriods") || "Доступны для добавления"}</span>
                </div>
              )}
            </div>

            {/* Матрица */}
            <div style={{ maxHeight: "60vh" }} className="fin-table-cover">
              <div
                className="fin-table"
                style={{
                  gridTemplateColumns: `200px repeat(${allPeriods.length}, minmax(80px, 1fr))`,
                }}
              >
                {/* Заголовок: Компания + даты */}
                <div style={{ ...firstColumnStyles, display: "flex", alignItems: "center" }}>
                  {"Компания"}
                </div>
                {allPeriods.map((period, idx) => (
                  <div
                    key={idx}
                    style={{
                      ...(isPendingPeriod(period) ? pendingColumnStyles : {}),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      fontWeight: 500,
                      fontSize: "12px",
                      padding: "5px",
                    }}
                  >
                    {formatDate(period)}
                  </div>
                ))}

                {/* Строки компаний */}
                {matrixData.companies?.map((company) => (
                  <React.Fragment key={company.clientId}>
                    <div style={{ ...firstColumnStyles, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                      <div style={{ fontWeight: 500, lineHeight: "1" }}>{company.clientName}</div>
                      {company.inn && (
                        <div style={{ fontSize: "11px", color: "#888", lineHeight: 0, marginTop: "0px" }}>
                          ИНН: {company.inn}
                        </div>
                      )}
                    </div>

                    {allPeriods.map((period, idx) => {
                      const isPending = isPendingPeriod(period);

                      // Для существующих периодов используем periodInclusion
                      // Для pending периодов используем pendingPeriodAvailability
                      let isIncluded = false;
                      if (isPending) {
                        isIncluded = Object.entries(company.pendingPeriodAvailability || {})
                          .some(([key, value]) =>
                            new Date(key).getTime() === new Date(period).getTime() && value
                          );
                      } else {
                        isIncluded = Object.entries(company.periodInclusion || {})
                          .some(([key, value]) =>
                            new Date(key).getTime() === new Date(period).getTime() && value
                          );
                      }

                      return (
                        <div
                          key={idx}
                          style={{
                            ...(isPending ? pendingColumnStyles : {}),
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          {isIncluded ? <CheckPointIcon /> : <EmptyPointIcon />}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}

                {/* Строка с кнопками добавления */}
                <div style={{
                  ...firstColumnStyles,
                  fontWeight: 600,
                  color: "#666",
                  borderTop: "1px solid #eee",
                  paddingTop: "10px",
                }}>
                  {t("consolidation.actions") || "Действия"}
                </div>
                {allPeriods.map((period, idx) => {
                  const isPending = isPendingPeriod(period);
                  const companiesWithData = getCompaniesWithDataCount(period);
                  const isExample = companiesWithData < 2;
                  const isCurrentlyAdding = addingPeriod &&
                    new Date(addingPeriod).getTime() === new Date(period).getTime();
                  const isDisabled = isCurrentlyAdding || isExample;

                  const Button = (
                    <button
                      onClick={() => handleAddPeriod(period)}
                      disabled={isDisabled}
                      style={{
                        padding: "4px 8px",
                        fontSize: "11px",
                        fontWeight: 500,
                        backgroundColor: isDisabled
                          ? "#ccc"
                          : "rgba(111, 164, 242, 1)",
                        color: "#fff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        transition: "all 0.2s",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {isCurrentlyAdding
                        ? "..."
                        : t("consolidation.add")}
                    </button>
                  );

                  return (
                    <div
                      key={idx}
                      style={{
                        ...(isPending ? pendingColumnStyles : {}),
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        borderTop: "1px solid #eee",
                        paddingTop: "10px",
                        gap: "4px",
                      }}
                    >
                      {isPending ? (
                        isExample ? (
                          <Tooltip title={t("consolidation.minTwoCompaniesWarning")}>
                            <span>{Button}</span>
                          </Tooltip>
                        ) : (
                          Button
                        )
                      ) : (
                        <div style={{
                          fontSize: "11px",
                          color: "#57C62F",
                          fontWeight: 500,
                        }}>
                          ✓
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            {t("FinReport.noData") || "Нет данных"}
          </div>
        )}
      </div>
    </ModalWrapper>
  );
};

export default PreviewGroupConsolidationModal;
